import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { AppLink } from '../components/Nav';
import { LinkIcon, PlusIcon, XIcon } from '../components/icons';
import {
  Badge,
  Button,
  CopyButton,
  EmptyState,
  ErrorState,
  ErrorText,
  Input,
  Loading,
  PageHeader,
  Select,
  cx,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { requestLinkUrl } from '../lib/env';
import { countLabel, shortDate } from '../lib/format';
import type { RequestLink } from '../lib/types';
import { qk } from '../queries/keys';
import {
  createRequestLink,
  fetchRequestLinks,
  updateRequestLink,
  type NewRequestLink,
} from '../queries/requestLinks';

/**
 * Request links — the "ask someone else for a video" surface.
 *
 * A link is a standing invitation: no Slate account on the other end, no app to
 * install. The recipient opens it, records or uploads, and the result lands in
 * the requester's library.
 */

function positiveInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.floor(value);
}

function CreateLinkForm({ onDone }: { onDone: () => void }) {
  const client = useQueryClient();
  const { activeOrg } = useWorkspace();

  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [maxVideos, setMaxVideos] = useState('');
  const [destination, setDestination] = useState<'personal' | 'org'>(activeOrg ? 'org' : 'personal');
  const [invalid, setInvalid] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: NewRequestLink) => createRequestLink(body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.requestLinks() });
      setLabel('');
      setExpiresInDays('');
      setMaxVideos('');
      onDone();
    },
  });

  const submit = () => {
    setInvalid(null);
    const trimmed = label.trim();
    if (!trimmed) {
      setInvalid('Give the link a label so you can tell it apart later.');
      return;
    }

    const body: NewRequestLink = { label: trimmed };

    if (expiresInDays.trim()) {
      const days = positiveInt(expiresInDays);
      if (days === null) {
        setInvalid('Expiry has to be a whole number of days, or left blank.');
        return;
      }
      body.expiresInDays = days;
    }

    if (maxVideos.trim()) {
      const cap = positiveInt(maxVideos);
      if (cap === null) {
        setInvalid('The video cap has to be a whole number, or left blank.');
        return;
      }
      body.maxVideos = cap;
    }

    if (destination === 'org' && activeOrg) body.orgId = activeOrg.id;

    create.mutate(body);
  };

  const landsIn =
    destination === 'org' && activeOrg
      ? `Videos will land in the ${activeOrg.name} team workspace, where everyone on the team can watch them.`
      : 'Videos will land in your personal library. Nobody else sees them unless you share them.';

  return (
    <form
      className="card mb-6 p-4 sm:p-5"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Input
            label="What are you asking for?"
            placeholder="Bug report from Priya"
            value={label}
            maxLength={120}
            autoFocus
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>

        <Input
          label="Expires in (days)"
          hint="Leave blank and it never expires."
          inputMode="numeric"
          placeholder="14"
          value={expiresInDays}
          onChange={(event) => setExpiresInDays(event.target.value)}
        />

        <Input
          label="Stop after (videos)"
          hint="Leave blank for as many as they send."
          inputMode="numeric"
          placeholder="1"
          value={maxVideos}
          onChange={(event) => setMaxVideos(event.target.value)}
        />

        {activeOrg ? (
          <div className="sm:col-span-2">
            <Select
              label="Where the videos land"
              value={destination}
              onChange={(event) =>
                setDestination(event.target.value === 'org' ? 'org' : 'personal')
              }
            >
              <option value="personal">My personal library</option>
              <option value="org">{activeOrg.name} — team workspace</option>
            </Select>
          </div>
        ) : null}
      </div>

      <p className="mt-3 text-caption text-chalk-faint">{landsIn}</p>

      {invalid ? (
        <div className="mt-3">
          <ErrorText>{invalid}</ErrorText>
        </div>
      ) : null}
      {create.isError ? (
        <div className="mt-3">
          <ErrorText>{errorMessage(create.error)}</ErrorText>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" busy={create.isPending}>
          Create link
        </Button>
        <Button variant="ghost" onClick={onDone} disabled={create.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function LinkRow({ link }: { link: RequestLink }) {
  const client = useQueryClient();
  const url = requestLinkUrl(link.token);

  const update = useMutation({
    mutationFn: (disabled: boolean) => updateRequestLink(link.id, { disabled }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.requestLinks() });
    },
  });

  const expired = link.expiresAt ? new Date(link.expiresAt).getTime() < Date.now() : false;
  const full = link.maxVideos !== null && link._count.videos >= link.maxVideos;

  return (
    <li className={cx('card p-4', link.disabled && 'opacity-60')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-h4 text-chalk">{link.label}</h3>
            {link.disabled ? <Badge tone="neutral">Disabled</Badge> : null}
            {!link.disabled && expired ? <Badge tone="caution">Expired</Badge> : null}
            {!link.disabled && !expired && full ? <Badge tone="caution">Cap reached</Badge> : null}
          </div>
          <p className="mt-1 text-caption text-chalk-faint">
            Created {shortDate(link.createdAt)}
            {link.expiresAt ? ` · ${expired ? 'Expired' : 'Expires'} ${shortDate(link.expiresAt)}` : ''}
          </p>
        </div>

        <Button
          size="sm"
          busy={update.isPending}
          onClick={() => update.mutate(!link.disabled)}
        >
          {link.disabled ? 'Turn back on' : 'Disable'}
        </Button>
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <code className="min-w-0 flex-1 break-all rounded bg-slate-900 px-2.5 py-1.5 font-mono text-caption text-chalk-dim">
          {url}
        </code>
        <CopyButton value={url} size="sm" label="Copy" className="shrink-0" />
      </div>

      <p className="mt-3 text-caption text-chalk-faint tnum">
        {countLabel(link._count.videos, 'video')} received
        {link.maxVideos !== null ? ` of ${link.maxVideos} allowed` : ''}
      </p>

      {update.isError ? (
        <div className="mt-2">
          <ErrorText>{errorMessage(update.error)}</ErrorText>
        </div>
      ) : null}
    </li>
  );
}

export function LinksPage() {
  const [creating, setCreating] = useState(false);
  const links = useQuery({ queryKey: qk.requestLinks(), queryFn: fetchRequestLinks });

  return (
    <>
      <PageHeader
        title="Request links"
        subtitle="Send one to anybody. They record, you get the video."
        actions={
          <Button
            variant={creating ? 'ghost' : 'primary'}
            onClick={() => setCreating((open) => !open)}
          >
            {creating ? <XIcon size={14} /> : <PlusIcon size={14} />}
            {creating ? 'Close' : 'New link'}
          </Button>
        }
      />

      {creating ? <CreateLinkForm onDone={() => setCreating(false)} /> : null}

      {links.isPending ? <Loading label="Loading your links" /> : null}

      {links.isError ? (
        <ErrorState
          title="Could not load your request links"
          message={errorMessage(links.error)}
          onRetry={() => void links.refetch()}
        />
      ) : null}

      {links.data && links.data.length === 0 && !creating ? (
        <EmptyState
          title="No request links yet"
          icon={<LinkIcon size={26} />}
          action={
            <Button variant="primary" onClick={() => setCreating(true)}>
              <PlusIcon size={14} />
              Create your first link
            </Button>
          }
        >
          <p>
            A request link is how you ask someone for a video when they do not have Slate. You send
            them a URL; they open it on a laptop or a phone, record their screen or camera — or
            upload a file they already have — and it lands in your library.
          </p>
          <p className="mt-2">
            Handy for bug reports, candidate intros, and customer walkthroughs. You can disable a
            link at any time, and old videos stay put when you do.
          </p>
        </EmptyState>
      ) : null}

      {links.data && links.data.length > 0 ? (
        <ul className="space-y-3">
          {links.data.map((link) => (
            <LinkRow key={link.id} link={link} />
          ))}
        </ul>
      ) : null}

      {links.data && links.data.length > 0 ? (
        <p className="mt-6 text-caption text-chalk-faint">
          Anyone with a link can send you a video, so treat it like a mailbox address.{' '}
          <AppLink to="/" className="text-signal hover:underline">
            Back to your library
          </AppLink>
        </p>
      ) : null}
    </>
  );
}
